import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';

import { cn } from '../../shared/lib/cn';
import { relativeTime } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { Claim } from '../../shared/ui/claim';
import { Page, PageHeader, Section } from '../../shared/ui/layout';
import { Note } from '../../shared/ui/note';
import { StatusBadge } from '../../shared/ui/status-badge';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { EMPTY_RUN_FILTERS, runFiltersToSearch } from '../../entities/run';
import { listIssues } from '../../entities/issue';
import { listSourceTargets, usedInKeys, type TSourceTarget } from '../../entities/used-in';
import {
  failureModeLine,
  generalSecrets,
  getSource,
  activityLine,
  sourceSecrets,
  VerificationLine,
  type TSource,
} from '../../entities/source';
import { SenderAddresses } from '../../features/ip-ranges';
import { GeneralAddress, useGeneralAddress } from '../../features/source-general-address';
import { IngestHostPicker } from '../../features/source-ingest-host';
import { SchemeBlock, useSourceScheme } from '../../features/source-scheme';
import { SourceSecrets } from '../../features/source-secrets';
import { SourceName, SourceSettings, useSourceSettings } from '../../features/source-settings';
import { SourceState } from '../../features/source-state';
import { TestSend } from '../../features/source-test-send';
import { UsedInFlows } from '../../widgets/used-in-flows';

interface IScreenProps {
  source: TSource;
  timeZone?: string;
  breadcrumb: ReactNode;
  onClients: () => void;
  onWatch: (requestId: string) => void;
}

const FLOWS_ID = 'source-flows';

// "2 flows take requests": the verb agrees with the count.
const takersWords = (targets: readonly TSourceTarget[]): string =>
  `${targets.length} ${targets.length === 1 ? 'flow takes' : 'flows take'} requests`;

const scrollToFlows = () =>
  document.getElementById(FLOWS_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

// Each step ticks off from real state; the block is gone once all three have.
const FirstRun = ({
  source,
  checked,
  taken,
}: {
  source: TSource;
  checked: boolean;
  taken: boolean;
}) => {
  const steps = [
    { done: checked, text: 'Say how requests are checked — pick the sender under Signing' },
    { done: taken, text: 'Start a flow from it, so its requests go somewhere' },
    {
      done: source.activity?.lastRequestAt != null,
      text: 'Point the sender at the address, or send a test request',
    },
  ];
  if (steps.every((step) => step.done)) return null;

  return (
    <Section title="Getting it working">
      <ol className="flex max-w-form-narrow flex-col gap-2">
        {steps.map((step) => (
          <li key={step.text} className="flex items-center gap-inset text-data">
            <span
              aria-hidden
              className={cn(
                'size-dot shrink-0 rounded-full',
                step.done ? 'bg-success' : 'bg-faint',
              )}
            />
            <span className={step.done ? 'text-faint line-through' : 'text-primary'}>
              {step.text}
            </span>
          </li>
        ))}
      </ol>
    </Section>
  );
};

const SourceScreen = ({ source, timeZone, breadcrumb, onClients, onWatch }: IScreenProps) => {
  const [testing, setTesting] = useState(false);
  const settings = useSourceSettings(source);
  // "#customer" from Events' Customer filter: the page scrolls in its own pane, not the window.
  const hash = useLocation({ select: (location) => location.hash });
  useEffect(() => {
    if (hash) document.getElementById(hash)?.scrollIntoView({ block: 'start' });
  }, [hash]);

  const secrets = useQuery({
    queryKey: ['source-secrets', source.id],
    queryFn: () => sourceSecrets(source.id),
  });
  const scheme = useSourceScheme(source.id);

  // The open auth issue used to ride on `source.verification.openIssue`; it is joined here now
  // that ingest no longer reads analytics for it (§4 phase 3, layer-restructure.md).
  const authIssue = useQuery({
    queryKey: ['issues', 'open-auth', source.id],
    queryFn: () =>
      listIssues({ view: 'active', errorClass: 'auth_failed', sourceId: source.id, limit: 1 }),
  });
  const openIssue = authIssue.data?.data[0] ?? null;

  const now = useMemo(() => Date.now(), [source.id]);

  const clientCount = source.verification.clientAddressCount;
  const keyless = source.verification.clientAddressesWithoutKey;
  // With no method that takes a secret a client has no key to lack; the count would call every one broken.
  const unchecked = !source.clientSecrets;
  const noKeysWhy = source.unauthenticated ? 'not checked' : 'no secrets · IP allowlist only';
  const address = useGeneralAddress(source);

  const own = generalSecrets(secrets.data ?? []);

  const saveChanges = () => {
    if (scheme.staged) scheme.save.mutate(scheme.staged);
    if (settings.canSave) settings.save.mutate();
  };

  // The Save button names what it will save: half this page applies the moment you click.
  // Every label starts with "Save": Signing's staged line says "press Save at the top".
  const pending = [...(scheme.staged ? ['signing'] : []), ...settings.sections];
  const saveLabel =
    pending.length === 0
      ? 'Save changes'
      : pending.length === 1
        ? `Save ${pending[0].toLowerCase()}`
        : `Save ${pending.length} changes`;

  // The Flows view of Events: what this source started, over the window its counts cover.
  const eventsSearch = {
    ...runFiltersToSearch({
      ...EMPTY_RUN_FILTERS,
      minutes: (source.activity?.windowHours ?? 24) * 60,
      sourceId: [source.id],
    }),
  };
  // The fanout's own answer, the one a test request gives: the live flows of this source.
  const targets = useQuery({
    queryKey: usedInKeys.targets(source.id),
    queryFn: () => listSourceTargets(source.id),
  });
  const taking = targets.data ?? [];
  const goesNowhere = source.state === 'active' && targets.isSuccess && taking.length === 0;
  const allPaused = taking.length > 0 && taking.every((target) => target.paused);

  return (
    <>
      <PageHeader
        width="wide"
        title={source.name}
        breadcrumbs={breadcrumb}
        description={
          <span className="flex flex-wrap items-center gap-3">
            <StatusBadge vocabulary="source" value={source.state} />
            <span>{activityLine('header', source.activity, { now, timeZone })}</span>
            <Link to="/events" search={eventsSearch} className="text-accent hover:underline">
              See its events
            </Link>
            {taking.length > 0 && (
              <button type="button" className="text-accent hover:underline" onClick={scrollToFlows}>
                {takersWords(taking)} from here
              </button>
            )}
          </span>
        }
        actions={
          <>
            <SourceState source={source} onFlows={scrollToFlows} />
            <Button variant="ghost" size="sm" onClick={() => setTesting(true)}>
              Send a test request
            </Button>
            <Button
              size="sm"
              title={pending.length > 1 ? pending.join(', ') : undefined}
              disabled={!settings.canSave && scheme.staged === null}
              waiting={settings.save.isPending || scheme.save.isPending}
              waitingLabel="Saving"
              onClick={saveChanges}
            >
              {saveLabel}
            </Button>
          </>
        }
      />

      <Page width="wide">
        {goesNowhere && (
          <Note tone="danger">
            No flow takes requests from here: they are accepted and kept, and go nowhere.{' '}
            <button type="button" className="text-primary underline" onClick={scrollToFlows}>
              Start a flow from it
            </button>
          </Note>
        )}
        {allPaused && (
          <Note>
            {takersWords(taking)} from here, all paused: requests are kept and wait until one is
            resumed.
          </Note>
        )}

        <FirstRun source={source} checked={scheme.checked} taken={taking.length > 0} />

        <SourceName settings={settings} />

        <Section
          title="Addresses"
          description="Two kinds. Which one a request arrives on is what tells us whose it is."
        >
          <IngestHostPicker className="mb-5 max-w-form-narrow" source={source} />
          <GeneralAddress className="max-w-form-narrow" source={source} address={address} />

          <div className="mt-3 flex max-w-form-narrow items-center gap-inset rounded-control bg-surface-sunken px-input py-3">
            <span
              aria-hidden
              className={cn(
                'size-dot shrink-0 rounded-full',
                clientCount === 0 || unchecked
                  ? 'bg-faint'
                  : keyless > 0
                    ? 'bg-danger'
                    : 'bg-success',
              )}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-data font-medium text-primary">Client addresses</span>
              <span
                className={cn(
                  'block font-mono text-meta',
                  keyless > 0 && !unchecked ? 'text-danger-text' : 'text-muted',
                )}
              >
                {clientCount === 0
                  ? unchecked
                    ? 'none yet · one per client'
                    : 'none yet · one per client, each with its own key'
                  : unchecked
                    ? `${clientCount.toLocaleString('en-US')}, one per client · ${noKeysWhy}`
                    : keyless > 0
                      ? `${clientCount.toLocaleString('en-US')} · ${keyless.toLocaleString('en-US')} without a working key`
                      : `${clientCount.toLocaleString('en-US')}, one per client · every one has a working key`}
              </span>
            </span>
            <Button variant="ghost" size="sm" className="h-control-xs shrink-0" onClick={onClients}>
              Open
            </Button>
          </div>
        </Section>

        <Section
          id="signing"
          title="Signing"
          description={
            !scheme.checked
              ? 'Nothing is checked yet: anything that reaches the address is accepted. Pick the sender to change that.'
              : scheme.senderName
                ? `Every request has to prove it came from ${scheme.senderName}. ${failureModeLine(source.onAuthFailure)}`
                : `Every request has to prove it came from the sender. ${failureModeLine(source.onAuthFailure)}`
          }
        >
          <SchemeBlock source={source} scheme={scheme} className="max-w-form-narrow" />

          <div className="mt-5 max-w-form-narrow">
            {secrets.isError && (
              <ErrorState error={secrets.error} onRetry={() => void secrets.refetch()} />
            )}
            {secrets.isPending && <LoadingState rows={3} />}
            {/* The saved scheme, never the staged one: the stored secret is verified against what is in force. */}
            {secrets.data && (
              <SourceSecrets
                sourceId={source.id}
                methodId={scheme.methodId}
                checked={scheme.checked}
                senderName={scheme.senderName}
                secretPrefix={scheme.secretPrefix}
                secrets={own}
                verification={source.verification}
                generalAddressOpen={address.open}
                onCloseGeneralAddress={address.close}
                closingGeneralAddress={address.pending}
                timeZone={timeZone}
                now={now}
              />
            )}
          </div>

          <SenderAddresses
            className="mt-5 max-w-form-narrow"
            keys={scheme.rangeKeys}
            filteringKeys={scheme.filteringKeys}
            now={now}
            timeZone={timeZone}
            only={scheme.addressesOnly}
            onRequire={(key) => scheme.requireAddresses.mutate(key)}
            requiring={scheme.requireAddresses.isPending}
            requireError={scheme.requireAddresses.error}
          />

          <VerificationLine
            className="mt-5 max-w-form-narrow"
            verification={source.verification}
            activity={source.activity}
            unchecked={!scheme.isPending && !scheme.isError && !scheme.checked}
            timeZone={timeZone}
          />

          {authIssue.isPending && (
            <div className="mt-3 max-w-form-narrow">
              <LoadingState rows={1} />
            </div>
          )}

          {authIssue.isError && (
            <Note tone="danger" className="mt-3 max-w-form-narrow">
              Could not check for an open verification issue.
            </Note>
          )}

          {openIssue && (
            <Claim size="card" className="mt-3 max-w-form-narrow">
              {openIssue.title}
              <span className="mt-2 block text-meta text-muted">
                {openIssue.eventCount.toLocaleString('en-US')} rejected requests since{' '}
                {relativeTime(openIssue.firstSeen, now)} ·{' '}
                <Link
                  to="/issues/$issueId"
                  params={{ issueId: openIssue.id }}
                  className="text-primary hover:underline"
                >
                  Open
                </Link>
              </span>
            </Claim>
          )}
        </Section>

        <div id={FLOWS_ID} className="scroll-mt-section">
          <UsedInFlows
            kind="source"
            id={source.id}
            name={source.name}
            timeZone={timeZone}
            className="max-w-form"
          />
        </div>

        <SourceSettings
          settings={settings}
          checked={scheme.checked}
          onAddChecking={() => scheme.setPicking(true)}
        />
      </Page>

      <TestSend
        source={source}
        open={testing}
        onOpenChange={setTesting}
        onWatch={(requestId) => {
          setTesting(false);
          onWatch(requestId);
        }}
        onFlows={scrollToFlows}
      />
    </>
  );
};

export const SourcePage = ({
  sourceId,
  ...rest
}: { sourceId: string } & Omit<IScreenProps, 'source'>) => {
  const source = useQuery({ queryKey: ['source', sourceId], queryFn: () => getSource(sourceId) });

  if (source.isError) {
    return (
      <div className="p-page">
        <ErrorState error={source.error} onRetry={() => void source.refetch()} />
      </div>
    );
  }

  if (!source.data) {
    return (
      <div className="p-page">
        <LoadingState rows={8} />
      </div>
    );
  }

  // Keyed by id: the settings draft inside must not survive a switch to another source.
  return <SourceScreen key={source.data.id} source={source.data} {...rest} />;
};
